import React, { useState } from 'react';
import { FiX, FiDownload, FiFileText, FiVideo, FiExternalLink } from 'react-icons/fi';

function DocumentationPreviewModal({ isOpen, onClose, files = [], videoUrl }) {
  const [activeIndex, setActiveIndex] = useState(videoUrl ? -1 : 0);

  if (!isOpen) return null;

  const getFullUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const base = (process.env.REACT_APP_IMAGEURL || '').replace(/\/$/, '');
    return `${base}/${path.replace(/^\//, '')}`;
  };

  const handleDownload = (file) => {
    const url = getFullUrl(file.file_path);
    window.open(url, '_blank');
  };

  const getYTSrc = (url) => {
    if (!url) return null;
    let videoId = '';
    if (url.includes('v=')) {
      videoId = url.split('v=')[1].split('&')[0];
    } else if (url.includes('be/')) {
      videoId = url.split('be/')[1].split('?')[0];
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const renderPreview = (file) => {
    if (!file) {
      if (videoUrl) {
         const ytSrc = getYTSrc(videoUrl);
         if (ytSrc) {
           return <iframe src={ytSrc} className="w-full h-[60vh] rounded shadow-lg" allowFullScreen title="Video Preview" />;
         }
         return (
           <div className="flex flex-col items-center justify-center p-12 bg-black/90 rounded-lg h-[60vh]">
             <FiVideo className="text-6xl text-white/50 mb-4" />
             <p className="text-white mb-6">External Video Link</p>
             <a 
               href={videoUrl} 
               target="_blank" 
               rel="noopener noreferrer"
               className="px-6 py-2 bg-main text-white rounded-md flex items-center gap-2 hover:bg-opacity-90 transition"
             >
               <FiExternalLink /> Watch External Video
             </a>
           </div>
         );
      }
      return null;
    }

    const ext = file.file_type.toLowerCase();
    const url = getFullUrl(file.file_path);

    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      return (
        <div className="flex justify-center items-center h-full bg-black/5 rounded">
           <img src={url} alt={file.file_name} className="max-h-full max-w-full object-contain shadow-md" />
        </div>
      );
    }

    // PDFs are now treated as downloadable documents because of X-Frame-Options restrictions on the server
    if (ext === 'pdf') {
       return (
        <div className="flex flex-col items-center justify-center p-12 bg-red-50/30 border border-red-100 rounded-lg min-h-[300px]">
          <FiFileText className="text-6xl text-red-400 mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2 truncate max-w-md text-center">{file.file_name}</h3>
          <p className="text-xs text-gray-500 mb-6 font-mono border bg-white px-3 py-1 rounded-full border-red-100">DOCUMENT FORMAT: {ext.toUpperCase()}</p>
          <button 
            onClick={() => handleDownload(file)}
            className="inline-flex items-center px-8 py-3 bg-red-600 text-white font-bold rounded-lg shadow-lg hover:bg-red-700 transition active:scale-95"
          >
            <FiDownload className="mr-2 text-xl" /> DOWNLOAD PDF
          </button>
          <p className="mt-4 text-[11px] text-gray-400 italic italic tracking-tight">Direct preview is blocked by server security; please download to view.</p>
        </div>
      );
    }

    if (ext === 'mp4') {
      return (
        <div className="flex justify-center items-center h-full bg-black/90 rounded">
          <video controls className="max-w-full max-h-[60vh]">
            <source src={url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center p-12 bg-gray-50 border border-gray-300 rounded-lg min-h-[300px]">
        <FiFileText className="text-6xl text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2 truncate max-w-md text-center">{file.file_name}</h3>
        <p className="text-sm text-gray-500 mb-6 font-mono lowercase">Format: {ext}</p>
        <button 
          onClick={() => handleDownload(file)}
          className="inline-flex items-center px-6 py-2 bg-main text-white font-medium rounded-md shadow hover:bg-opacity-90 transition"
        >
          <FiDownload className="mr-2" /> Download
        </button>
      </div>
    );
  };

  const hasItems = files.length > 0 || videoUrl;
  const activeFile = activeIndex === -1 ? null : files[activeIndex];

  return (
    <div className="fixed inset-0 z-[100] flex justify-center items-center p-4 bg-gray-900/80 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh] overflow-hidden">
        
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            Document Preview
            {files.length + (videoUrl ? 1 : 0) > 1 && (
              <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {files.length + (videoUrl ? 1 : 0)} Available Items
              </span>
            )}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
            <FiX className="text-2xl" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
           {/* Navigation Sidebar if multiple items */}
           {(files.length + (videoUrl ? 1 : 0) > 1) && (
              <div className="w-1/4 border-r bg-gray-50 overflow-y-auto">
                 <ul className="divide-y divide-gray-200">
                    {videoUrl && (
                      <li 
                        onClick={() => setActiveIndex(-1)}
                        className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${activeIndex === -1 ? 'bg-red-50 border-l-4 border-red-500' : 'hover:bg-gray-100 border-l-4 border-transparent'}`}
                      >
                        <FiVideo className={`text-xl ${activeIndex === -1 ? 'text-red-500' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate font-semibold ${activeIndex === -1 ? 'text-red-600' : 'text-gray-700'}`}>External Video</p>
                          <p className="text-[10px] text-gray-500 uppercase">URL Link</p>
                        </div>
                      </li>
                    )}
                    {files.map((file, idx) => (
                      <li 
                        key={idx} 
                        onClick={() => setActiveIndex(idx)}
                        className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${activeIndex === idx ? 'bg-blue-50 border-l-4 border-main' : 'hover:bg-gray-100 border-l-4 border-transparent'}`}
                      >
                        <FiFileText className={`text-xl ${activeIndex === idx ? 'text-main' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate font-semibold ${activeIndex === idx ? 'text-main' : 'text-gray-700'}`}>{file.file_name}</p>
                          <p className="text-[10px] text-gray-500 uppercase">{file.file_type}</p>
                        </div>
                      </li>
                    ))}
                 </ul>
              </div>
           )}

           <div className={`p-6 overflow-y-auto bg-gray-100 flex-1`}>
              {hasItems ? (
                <div className="bg-white rounded-lg shadow-sm h-full min-h-[50vh]">
                  {renderPreview(activeFile)}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-12 text-gray-500 italic">
                   No preview available for this entry.
                </div>
              )}
           </div>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
           <button 
             onClick={onClose}
             className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
           >
              Close
           </button>
        </div>
      </div>
    </div>
  );
}

export default DocumentationPreviewModal;
